/**
 * Conversation Prompt — Cycle-based prompt with optional journal input
 *
 * Shows once per cycle. Dismissible but not permanently.
 * Includes optional journal input for shared/private notes.
 */
import { useState } from "react";
import { usePrivacy } from "./PrivacyProvider";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { MessageCircle, X, BookOpen, Lock } from "lucide-react";
import { toast } from "sonner";

export function ConversationPrompt({ promptKey, showJournal = false }) {
  const { prompts, dismissPrompt, saveJournal, isSolo } = usePrivacy();
  const [journalOpen, setJournalOpen] = useState(false);
  const [note, setNote] = useState("");
  const [visibility, setVisibility] = useState("shared");
  const [saving, setSaving] = useState(false);

  const text = prompts[promptKey];
  if (!text) return null;

  const handleDismiss = () => {
    dismissPrompt(promptKey);
  };

  const handleSaveNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    const result = await saveJournal(note.trim(), promptKey, visibility);
    if (result) {
      toast.success("Captured. This builds over time.");
      setNote("");
      setJournalOpen(false);
      handleDismiss();
    }
    setSaving(false);
  };

  return (
    <div
      className="relative bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl p-4 mb-4"
      data-testid={`conversation-prompt-${promptKey}`}
    >
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-amber-600/40 hover:text-amber-600 transition-colors"
        data-testid={`dismiss-prompt-${promptKey}`}
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <MessageCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-amber-800 dark:text-amber-300 pr-6">{text}</p>

          {showJournal && !journalOpen && (
            <button
              onClick={() => setJournalOpen(true)}
              className="mt-2 text-xs text-amber-700/60 hover:text-amber-700 flex items-center gap-1 transition-colors"
              data-testid={`journal-open-${promptKey}`}
            >
              <BookOpen className="w-3 h-3" />
              Add a note
            </button>
          )}

          {showJournal && journalOpen && (
            <div className="mt-3 space-y-2">
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={isSolo ? "What's on your mind?" : "What matters most right now?"}
                className="min-h-[60px] text-sm bg-white dark:bg-background"
                data-testid={`journal-input-${promptKey}`}
              />
              {!isSolo && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setVisibility("shared")}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-colors ${
                      visibility === "shared" ? "bg-[#1a4d3e]/10 text-[#1a4d3e]" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="journal-vis-shared"
                  >
                    <MessageCircle className="w-3 h-3" /> Shared note
                  </button>
                  <button
                    onClick={() => setVisibility("private")}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-colors ${
                      visibility === "private" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="journal-vis-private"
                  >
                    <Lock className="w-3 h-3" /> Private note
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveNote} disabled={saving || !note.trim()} className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90" data-testid={`journal-save-${promptKey}`}>
                  {saving ? "Saving..." : "Save note"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setJournalOpen(false); setNote(""); }}>
                  Skip
                </Button>
              </div>
            </div>
          )}

          {!showJournal && (
            <div className="mt-2">
              <Button size="sm" variant="ghost" onClick={handleDismiss} className="text-xs text-amber-700/60 hover:text-amber-700" data-testid={`prompt-continue-${promptKey}`}>
                {isSolo ? "Continue" : "We're good"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
